import { useTracker } from '@/core/tracking';
import {
  cookieBannerResolvedBeforeSessionState,
  cookieBannerResolvedState,
} from '@/stores/cookieLoginCtaStore';
import Cookies from 'js-cookie';
import { useEffect, useRef } from 'react';
import { useSetRecoilState } from 'recoil';

type CookieConsentAction =
  | 'accepted'
  | 'rejected'
  | 'customized'
  | 'customizing';

const getCookieConsentAction = (
  target: EventTarget | null
): CookieConsentAction | undefined => {
  if (!(target instanceof Element)) return undefined;

  if (
    target.closest(
      '#onetrust-accept-btn-handler, #accept-recommended-btn-handler'
    )
  ) {
    return 'accepted';
  }
  if (
    target.closest('.save-preference-btn-handler.onetrust-close-btn-handler')
  ) {
    return 'customized';
  }
  if (
    target.closest(
      '#onetrust-close-btn-container > button, .ot-pc-refuse-all-handler'
    )
  ) {
    return 'rejected';
  }
  if (target.closest('#onetrust-pc-btn-handler, .ot-floating-button__open')) {
    return 'customizing';
  }

  return undefined;
};

export const usePrivacyConsentEventsListener = () => {
  const { track } = useTracker();
  const setCookieBannerResolved = useSetRecoilState(cookieBannerResolvedState);
  const setCookieBannerResolvedBeforeSession = useSetRecoilState(
    cookieBannerResolvedBeforeSessionState
  );
  const initialCookieBannerDismissed = useRef<boolean>();

  useEffect(() => {
    if (initialCookieBannerDismissed.current === undefined) {
      initialCookieBannerDismissed.current = Boolean(
        Cookies.get('OptanonAlertBoxClosed')
      );
    }
    const wasCookieBannerDismissed = initialCookieBannerDismissed.current;
    setCookieBannerResolvedBeforeSession(wasCookieBannerDismissed);
    const onCookieConsentClick = (event: MouseEvent) => {
      const action = getCookieConsentAction(event.target);
      if (!action) return;

      if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'development') {
        Cookies.set('OptanonAlertBoxClosed', new Date().toISOString());
      }

      track('cookies', action);
      if (action !== 'customizing' && !wasCookieBannerDismissed) {
        setCookieBannerResolved(true);
      }
    };

    document.addEventListener('click', onCookieConsentClick, true);

    return () => {
      document.removeEventListener('click', onCookieConsentClick, true);
    };
  }, [setCookieBannerResolved, setCookieBannerResolvedBeforeSession, track]);
};
